import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../redux/reducers";
import { AppDispatch } from "../redux/store";
import { fetchEmployeeAttendanceAction } from "../redux/actions/employeeAttendanceAction";
import Main from "../components/custom/Main";
import PageContainer from "../components/custom/PageContainer";
import CollapseCard from "../components/custom/CollapseCard";
import { Table } from "react-bootstrap";
import { fetchEmployeesAction } from "../redux/actions/employeeActions";
import "../styles/Attendance.css";
import { LocalFormatDate } from "../config/functions";

const AttendanceAdmin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const attendanceList = useSelector(
    (state: StoreState) => state.employeeAttendance,
  );
  const employeeList = useSelector((state: StoreState) => state.employee);

  useEffect(() => {
    dispatch(fetchEmployeeAttendanceAction());
    dispatch(fetchEmployeesAction());
    const interval = setInterval(() => {
      dispatch(fetchEmployeeAttendanceAction());
    }, 5000);
    return () => clearInterval(interval);
  }, [dispatch]);

  const sortedAttendance = [...attendanceList].sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    if (dateB !== dateA) {
      return dateB - dateA;
    }
    const aTime = a.checkin ? new Date(a.checkin).getTime() : 0;
    const bTime = b.checkin ? new Date(b.checkin).getTime() : 0;
    return bTime - aTime;
  });

  const formatDuration = (mins: number) => {
    if (!mins) return "—";
    const totalMs = mins * 60 * 1000;
    const hours = Math.floor(totalMs / (1000 * 60 * 60));
    const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((totalMs % (1000 * 60)) / 1000);
    return `${hours}h ${minutes}m ${seconds}s`;
  };
  const todayStr = new Date().toLocaleDateString("sv-SE");

  const todayAttendance = attendanceList.filter(
    (att) => new Date(att.date).toLocaleDateString("sv-SE") === todayStr,
  );

  const checkinList = todayAttendance.filter(
    (att) => att.status?.toLowerCase() === "checkedin",
  );

  const breakList = todayAttendance.filter(
    (att) => att.status?.toLowerCase() === "break",
  );

  const checkoutList = todayAttendance.filter(
    (att) => att.status?.toLowerCase() === "checkedout",
  );

  const getEmployeeName = (empId: string) => {
    const emp = employeeList.find((e) => e.employeeId === empId);
    return emp?.name || empId;
  };

  return (
    <Main>
      <PageContainer title="Attendance">
        <CollapseCard title="Attendance view">
          <div className="container">
            <div className="attendance-card-row mt-4 mb-3">
              {/* Checkin Card */}
              <div
                className="card p-3 shadow first-box"
                style={{ width: "30%" }}
              >
                <h5 style={{ color: "green" }}>
                  Checkin ({checkinList.length})
                </h5>
                <ul>
                  {checkinList.map((att) => (
                    <li key={att.id}>{getEmployeeName(att.employeeId)}</li>
                  ))}
                </ul>
              </div>

              {/* Break Card */}
              <div
                className="card p-3 shadow second-box"
                style={{ width: "30%" }}
              >
                <h5 style={{ color: "orange" }}>Break ({breakList.length})</h5>
                <ul>
                  {breakList.map((att) => (
                    <li key={att.id}>{getEmployeeName(att.employeeId)}</li>
                  ))}
                </ul>
              </div>

              {/* Checkout Card */}
              <div
                className="card p-3 shadow third-box"
                style={{ width: "30%" }}
              >
                <h5 style={{ color: "red" }}>
                  Checkout ({checkoutList.length})
                </h5>
                <ul>
                  {checkoutList.map((att) => (
                    <li key={att.id}>{getEmployeeName(att.employeeId)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </CollapseCard>
        <CollapseCard title="Attendance List">
          <Table
            striped
            bordered
            hover
            responsive
            className="custom-employee-table"
          >
            <thead>
              <tr>
                <th>s.No</th>
                <th>EmployeeId</th>
                <th>EmployeeName</th>
                <th>Date</th>
                <th>Checkin</th>
                <th>Checkout</th>
                <th>Break</th>
                <th>WorkingHours</th>
              </tr>
            </thead>
            <tbody>
              {sortedAttendance.map((att, index) => {
                const employee = employeeList.find(
                  (emp) => emp.employeeId === att.employeeId,
                );
                const employeeName = employee?.name || "Unknown";

                const localDateStr = (date: Date) =>
                  date.toLocaleDateString("sv-SE");

                const today = localDateStr(new Date());
                const formattedAttDate = localDateStr(new Date(att.date));

                // Status Color Logic
                const status = att.status?.toLowerCase();
                let statusColor = "gray";
                if (formattedAttDate === today && status === "checkedin") {
                  statusColor = "green";
                } else if (formattedAttDate === today && status === "break") {
                  statusColor = "orange";
                } else if (
                  formattedAttDate === today &&
                  status === "checkedout"
                ) {
                  statusColor = "red";
                } else if (status === "checkedout") {
                  statusColor = "red";
                }

                // Working Hours Calculation
                const calculateWorkingHours = () => {
                  const checkin = att.checkin ? new Date(att.checkin) : null;
                  const checkout = att.checkout ? new Date(att.checkout) : null;

                  if (checkin && checkout) {
                    const difMs = checkout.getTime() - checkin.getTime();
                    const breakMs = (att.breakHrs || 0) * 60 * 1000;
                    const totalMs = difMs - breakMs;

                    if (totalMs > 0) {
                      const hours = Math.floor(totalMs / (1000 * 60 * 60));
                      const minutes = Math.floor(
                        (totalMs % (1000 * 60 * 60)) / (1000 * 60),
                      );
                      const seconds = Math.floor(
                        (totalMs % (1000 * 60)) / 1000,
                      );
                      return `${hours}h ${minutes}m ${seconds}s`;
                    }
                  }
                  return "—";
                };

                return (
                  <tr key={att.id}>
                    <td>{index + 1}</td>
                    <td>
                      <label
                        className="d-flex align-items-center"
                        style={{ gap: "1rem" }}
                      >
                        <input
                          type="radio"
                          name={`employeeSelect-${att.employeeId}-${att.date}`}
                          style={{ accentColor: statusColor }}
                          checked
                          readOnly
                        />
                        <span>{att.employeeId}</span>
                      </label>
                    </td>
                    <td>{employeeName}</td>
                    <td>{LocalFormatDate(att.date)}</td>
                    <td>
                      {att.checkin
                        ? new Date(att.checkin).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td>
                      {att.checkout
                        ? new Date(att.checkout).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "—"}
                    </td>
                    <td>{formatDuration(att.breakHrs)}</td>
                    <td>{calculateWorkingHours()}</td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </CollapseCard>
      </PageContainer>
    </Main>
  );
};
export default AttendanceAdmin;
